import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import React, { useEffect, useMemo, useState } from "react";

import useThemeStore from "@/shared/stores/useThemeStore";
import SingleCardFanThankYouPanel from "@/cards/single-album-card/component/SingleCardFanThankYouPanel";

type ImageExperienceData =
  | {
      image: {
        imageUrl: string;
        title?: string;
        description?: string;
      };
    }
  | {
      imageGrid: {
        images: string[];
        description?: string;
      };
    };

export type TaasStaiExpImageClosingNote = {
  /** Logged-in fan display name (first/last or username). */
  fanDisplayName?: string | null;

  /** Fan profile photo for a more personal moment (optional). */
  fanAvatarUrl?: string | null;

  /** Optional image (e.g. handwritten sign). */
  signatureImageUrl?: string;
};

type Props = {
  data: ImageExperienceData;

  /** Shown at the bottom of the slide. */
  closingNote?: TaasStaiExpImageClosingNote;

  /** Guard rail: fan thank-you panel enabled by parent template gate. */
  isSingleCard?: boolean;
};

export const TaasExpImage: React.FC<Props> = ({ data, closingNote, isSingleCard = false }) => {
  const { darkMode } = useThemeStore();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  const [showFullDescription, setShowFullDescription] = useState(false);

  /**
   * Expanded image viewer
   */
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  /**
   * Convert both supported data structures into one image array.
   */
  const imageUrls: string[] = useMemo(() => {
    if ("image" in data) {
      return data.image.imageUrl ? [data.image.imageUrl] : [];
    }

    if ("imageGrid" in data) {
      return Array.isArray(data.imageGrid.images) ? data.imageGrid.images.filter(Boolean) : [];
    }

    return [];
  }, [data]);

  const title: string | undefined = "image" in data ? data.image.title : undefined;

  const descriptionRaw: string | undefined =
    "image" in data ? data.image.description : data.imageGrid.description;

  /**
   * Remove HTML from description.
   */
  const stripHtmlTags = (html: string) => {
    if (!html) return "";

    try {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      return tmp.textContent || tmp.innerText || "";
    } catch {
      return html;
    }
  };

  const description = descriptionRaw ? stripHtmlTags(descriptionRaw) : undefined;

  const showThankYou = Boolean(isSingleCard && closingNote);

  const showCaption = Boolean(title || description);

  /**
   * Only show the first 4 images in the inline gallery.
   */
  const visibleImages = imageUrls.slice(0, 4);

  /**
   * Number of images hidden behind the +N overlay.
   */
  const remainingImageCount = Math.max(imageUrls.length - 4, 0);

  const textColor = darkMode ? "var(--neutral-100)" : "var(--neutral-900)";

  const secondaryTextColor = darkMode ? "var(--neutral-300)" : "var(--neutral-600)";

  /**
   * Handle image errors.
   */
  const handleImageError = (index: number) => {
    setImageErrors((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  /**
   * Open expanded viewer.
   */
  const openImage = (index: number) => {
    setExpandedIndex(index);
  };

  /**
   * Close expanded viewer.
   */
  const closeImage = () => {
    setExpandedIndex(null);
  };

  /**
   * Previous image.
   */
  const goPrevious = () => {
    setExpandedIndex((current) => {
      if (current === null || imageUrls.length === 0) {
        return current;
      }

      return current === 0 ? imageUrls.length - 1 : current - 1;
    });
  };

  /**
   * Next image.
   */
  const goNext = () => {
    setExpandedIndex((current) => {
      if (current === null || imageUrls.length === 0) {
        return current;
      }

      return current === imageUrls.length - 1 ? 0 : current + 1;
    });
  };

  /**
   * Keyboard navigation for expanded viewer.
   */
  useEffect(() => {
    if (expandedIndex === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeImage();
      }

      if (event.key === "ArrowLeft") {
        goPrevious();
      }

      if (event.key === "ArrowRight") {
        goNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [expandedIndex]);

  return (
    <>
      {/* =========================================================
          MAIN EXPERIENCE CONTENT
          ========================================================= */}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
          p: { xs: "50px 22px 24px 22px", sm: "50px 22px 24px 22px" },
        }}
      >
        {/* =======================================================
            DESCRIPTION
            ======================================================= */}
        {showCaption && (
          <Stack
            spacing={0.75}
            sx={{
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {title && (
              <Typography
                sx={{
                  color: textColor,
                  fontSize: { xs: "1.25rem", sm: "1.45rem" },
                  fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                {title}
              </Typography>
            )}

            {description && (
              <Box>
                <Typography
                  sx={{
                    color: secondaryTextColor,
                    fontSize: { xs: "1rem", sm: "1.05rem" },
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",

                    ...(showFullDescription
                      ? {}
                      : {
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }),
                  }}
                >
                  {description}
                </Typography>

                {description.length > 180 && (
                  <Button
                    onClick={() => setShowFullDescription((prev) => !prev)}
                    sx={{
                      mt: 0.5,
                      p: 0,
                      minWidth: "auto",

                      color: darkMode ? "var(--neutral-200)" : "var(--neutral-700)",

                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: "none",

                      "&:hover": {
                        background: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {showFullDescription ? "See less" : "See more"}
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        )}

        <Box
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,

            display: "grid",

            gridTemplateColumns: imageUrls.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",

            gridTemplateRows:
              imageUrls.length <= 2
                ? "1fr"
                : imageUrls.length === 3
                  ? "minmax(0, 1.6fr) minmax(0, 1fr)"
                  : "repeat(2, minmax(0, 1fr))",

            gap: 1,

            overflow: "hidden",
          }}
        >
          {visibleImages.map((url, index) => {
            const isThreeImages = imageUrls.length === 3;

            const isFirstImage = index === 0;

            const showMore = imageUrls.length > 4 && index === 3;

            return (
              <Box
                key={`${url}-${index}`}
                onClick={() => openImage(index)}
                sx={{
                  position: "relative",

                  width: "100%",
                  height: "100%",

                  minWidth: 0,
                  minHeight: 0,

                  overflow: "hidden",

                  borderRadius: 2,

                  cursor: "pointer",

                  /**
                   * 3 images:
                   * First image spans the complete top row.
                   */
                  ...(isThreeImages &&
                    isFirstImage && {
                      gridColumn: "1 / -1",
                    }),

                  bgcolor: darkMode ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",

                  "&:hover img": {
                    transform: "scale(1.02)",
                  },

                  "@media (hover: none)": {
                    "&:hover img": {
                      transform: "none",
                    },
                  },
                }}
              >
                <Box
                  component="img"
                  src={url}
                  alt=""
                  draggable={false}
                  onContextMenu={(event) => {
                    event.preventDefault();
                  }}
                  onError={() => handleImageError(index)}
                  sx={{
                    width: "100%",
                    height: "100%",

                    objectFit: "cover",

                    display: "block",

                    userSelect: "none",
                    pointerEvents: "none",

                    transition: "transform 200ms ease",
                  }}
                />

                {/* +N overlay */}
                {showMore && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,

                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",

                      background: "rgba(0,0,0,0.48)",

                      backdropFilter: "blur(2px)",

                      pointerEvents: "none",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: {
                          xs: 24,
                          sm: 30,
                        },
                        fontWeight: 700,
                      }}
                    >
                      +{remainingImageCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* =======================================================
            THANK YOU PANEL
            ======================================================= */}
        {showThankYou && closingNote ? (
          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
            }}
          >
            <SingleCardFanThankYouPanel
              fanDisplayName={closingNote.fanDisplayName}
              fanAvatarUrl={closingNote.fanAvatarUrl}
              darkMode={darkMode}
              signatureImageUrl={closingNote.signatureImageUrl}
              embedded
            />
          </Box>
        ) : null}
      </Box>

      {/* =========================================================
          EXPANDED IMAGE VIEWER
          ========================================================= */}
      <Dialog
        open={expandedIndex !== null}
        onClose={closeImage}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#111",
            color: "#fff",
            overflow: "hidden",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(0,0,0,0.92)",
            },
          },
        }}
      >
        {/* =======================================================
            HEADER
            ======================================================= */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,

            height: {
              xs: 58,
              sm: 64,
            },

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            px: {
              xs: 1.5,
              sm: 2.5,
            },

            zIndex: 10,

            bgcolor: "rgba(0,0,0,0.28)",
            backdropFilter: "blur(10px)",

            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Georgia, serif",

              fontSize: {
                xs: 17,
                sm: 20,
              },

              fontWeight: 700,
            }}
          >
            Photo Book
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            {expandedIndex !== null && (
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.72)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {expandedIndex + 1} / {imageUrls.length}
              </Typography>
            )}

            <IconButton
              onClick={closeImage}
              aria-label="Close"
              sx={{
                color: "#fff",

                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* =======================================================
            IMAGE AREA
            ======================================================= */}
        {expandedIndex !== null && imageUrls[expandedIndex] && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              px: {
                xs: 2,
                sm: 8,
              },

              pt: {
                xs: 8,
                sm: 9,
              },

              pb: {
                xs: 3,
                sm: 5,
              },
            }}
          >
            <Box
              component="img"
              src={imageUrls[expandedIndex]}
              alt=""
              onContextMenu={(event) => {
                event.preventDefault();
              }}
              draggable={false}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",

                width: "auto",
                height: "auto",

                objectFit: "contain",

                borderRadius: {
                  xs: 1.5,
                  sm: 2,
                },

                userSelect: "none",

                boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
              }}
            />
          </Box>
        )}

        {/* =======================================================
            PREVIOUS BUTTON
            ======================================================= */}
        {imageUrls.length > 1 && (
          <IconButton
            onClick={goPrevious}
            aria-label="Previous image"
            sx={{
              position: "absolute",

              left: {
                xs: 8,
                sm: 20,
              },

              top: "50%",

              transform: "translateY(-50%)",

              zIndex: 10,

              width: {
                xs: 42,
                sm: 52,
              },

              height: {
                xs: 42,
                sm: 52,
              },

              color: "#fff",

              bgcolor: "rgba(0,0,0,0.25)",

              backdropFilter: "blur(8px)",

              border: "1px solid rgba(255,255,255,0.12)",

              "&:hover": {
                bgcolor: "rgba(0,0,0,0.5)",
              },
            }}
          >
            <ChevronLeftRoundedIcon
              sx={{
                fontSize: {
                  xs: 28,
                  sm: 34,
                },
              }}
            />
          </IconButton>
        )}

        {/* =======================================================
            NEXT BUTTON
            ======================================================= */}
        {imageUrls.length > 1 && (
          <IconButton
            onClick={goNext}
            aria-label="Next image"
            sx={{
              position: "absolute",

              right: {
                xs: 8,
                sm: 20,
              },

              top: "50%",

              transform: "translateY(-50%)",

              zIndex: 10,

              width: {
                xs: 42,
                sm: 52,
              },

              height: {
                xs: 42,
                sm: 52,
              },

              color: "#fff",

              bgcolor: "rgba(0,0,0,0.25)",

              backdropFilter: "blur(8px)",

              border: "1px solid rgba(255,255,255,0.12)",

              "&:hover": {
                bgcolor: "rgba(0,0,0,0.5)",
              },
            }}
          >
            <ChevronRightRoundedIcon
              sx={{
                fontSize: {
                  xs: 28,
                  sm: 34,
                },
              }}
            />
          </IconButton>
        )}
      </Dialog>
    </>
  );
};
